import React, { useEffect, useState } from 'react';
import { insuranceApi } from './api';
import ModalShell from './ModalShell';

/**
 * Modal sencillo para previsualizar un PDF de asset_documents.
 *
 * Recibe `documentId` y al abrir pide al backend una signed URL temporal
 * (60 min). Renderiza el PDF en un <object> con la toolbar nativa del
 * navegador deshabilitada (mismo patrón que DocumentoCard).
 *
 * El padre controla `visible` y `onHide`. Cuando `documentId` cambia se
 * vuelve a pedir la URL.
 */
export default function PdfViewerModal({ visible, onHide, documentId, fileName }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signedUrl, setSignedUrl] = useState(null);
  const [docFileName, setDocFileName] = useState(fileName || '');

  useEffect(() => {
    if (!visible || !documentId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSignedUrl(null);

    insuranceApi.getDocumentUrl(documentId)
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.success && res.data.signed_url) {
          setSignedUrl(res.data.signed_url);
          if (res.data.file_name) setDocFileName(res.data.file_name);
        } else {
          setError(res?.data?.message || 'No se pudo obtener el documento.');
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.message || e.message || 'Error al cargar el documento.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [visible, documentId]);

  // Reset al cerrar
  useEffect(() => {
    if (!visible) {
      setSignedUrl(null);
      setError(null);
      setLoading(false);
    }
  }, [visible]);

  return (
    <ModalShell visible={visible} onClose={onHide} stacked width={960} height={720}>
      <button className="modal-close" onClick={onHide}>
        <i className="pi pi-times" />
      </button>

      <div style={{ padding: '20px 24px 12px' }}>
        <div className="eyebrow" style={{ marginBottom: 4 }}>Documento</div>
        <div style={{
          fontSize: 16, fontWeight: 600, color: 'var(--pei-text)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {docFileName || `Documento #${documentId || '?'}`}
        </div>
      </div>

      <div style={{
        flex: 1, minHeight: 0,
        padding: '0 24px 20px',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          flex: 1, minHeight: 0,
          background: 'var(--pei-bg-soft)',
          border: '1px solid var(--pei-gray-5)',
          borderRadius: 12,
          overflow: 'hidden',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {loading && (
            <div style={{ textAlign: 'center', color: 'var(--pei-text-helper)' }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: 24 }} />
              <div style={{ marginTop: 8, fontSize: 13 }}>Cargando documento…</div>
            </div>
          )}
          {!loading && error && (
            <div style={{ textAlign: 'center', color: 'var(--pei-danger)', padding: 20 }}>
              <i className="pi pi-exclamation-triangle" style={{ fontSize: 24 }} />
              <div style={{ marginTop: 8, fontSize: 13 }}>{error}</div>
            </div>
          )}
          {!loading && !error && signedUrl && (
            <object
              data={`${signedUrl}#toolbar=1&navpanes=0&view=FitH`}
              type="application/pdf"
              width="100%"
              height="100%"
              style={{ border: 'none', minHeight: 500 }}
            >
              <div style={{ padding: 20, textAlign: 'center' }}>
                <i className="pi pi-file-pdf" style={{ fontSize: 32, color: 'var(--pei-danger)' }} />
                <div style={{ marginTop: 8, fontSize: 13 }}>
                  El navegador no pudo embed el PDF.{' '}
                  <a href={signedUrl} target="_blank" rel="noopener noreferrer">Abrir en pestaña nueva</a>.
                </div>
              </div>
            </object>
          )}
        </div>
      </div>

      <div style={{
        padding: '12px 24px 18px', borderTop: '1px solid var(--pei-gray-5)',
        display: 'flex', justifyContent: 'flex-end', gap: 10, background: 'white',
      }}>
        {signedUrl && (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pei-btn pei-btn-ghost"
            style={{ textDecoration: 'none' }}
          >
            <i className="pi pi-external-link" style={{ fontSize: 13 }} /> Abrir en pestaña
          </a>
        )}
        <button className="pei-btn pei-btn-ring" onClick={onHide}>Cerrar</button>
      </div>
    </ModalShell>
  );
}
